import React, { useState, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Search, Download, CheckCircle, AlertTriangle, XCircle,
  Globe, FileText, Smartphone, Zap, Link2, Image, Share2,
  ChevronDown, ChevronUp, ArrowRight, BarChart2, Code,
  Shield, Clock, RefreshCw, Star, TrendingUp
} from 'lucide-react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import PageMeta from '../components/PageMeta'
import './SEOToolPage.css'

const API_BASE = import.meta.env.VITE_API_URL || 'https://rdsstudio.co.za/api'

/* ── Helpers ── */
const scoreColor = (score) => {
  if (score >= 80) return '#22c55e'
  if (score >= 60) return '#eab308'
  if (score >= 40) return '#f97316'
  return '#ef4444'
}

const scoreLabel = (score) => {
  if (score >= 80) return 'Excellent'
  if (score >= 60) return 'Good'
  if (score >= 40) return 'Needs Work'
  return 'Poor'
}

const bytesToKB = (bytes) => (bytes / 1024).toFixed(1)

/* ── Score gauge SVG ── */
function ScoreGauge({ score }) {
  const radius = 54
  const cx = 64
  const cy = 64
  const circumference = 2 * Math.PI * radius
  const half = circumference / 2
  const offset = half - (score / 100) * half
  const color = scoreColor(score)

  return (
    <div className="seo-gauge">
      <svg viewBox="0 0 128 128" width="160" height="160">
        {/* Background arc */}
        <path
          d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
          fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10"
          strokeLinecap="round"
        />
        {/* Score arc */}
        <path
          d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
          fill="none" stroke={color} strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={`${half} ${half}`}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1s ease, stroke 0.3s' }}
        />
      </svg>
      <div className="seo-gauge__center">
        <span className="seo-gauge__score" style={{ color }}>{score}</span>
        <span className="seo-gauge__label" style={{ color }}>{scoreLabel(score)}</span>
      </div>
    </div>
  )
}

/* ── Category score bar ── */
function CategoryBar({ label, score, max, icon: Icon }) {
  const pct = Math.round((score / max) * 100)
  const color = scoreColor(pct)
  return (
    <div className="seo-cat-bar">
      <div className="seo-cat-bar__header">
        <span className="seo-cat-bar__icon"><Icon size={15} /></span>
        <span className="seo-cat-bar__label">{label}</span>
        <span className="seo-cat-bar__score" style={{ color }}>{score}<span className="seo-cat-bar__max">/{max}</span></span>
      </div>
      <div className="seo-cat-bar__track">
        <div className="seo-cat-bar__fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  )
}

/* ── Collapsible section ── */
function Section({ title, icon: Icon, badge, badgeColor, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className={`seo-section ${open ? 'open' : ''}`}>
      <button className="seo-section__toggle" onClick={() => setOpen(o => !o)}>
        <span className="seo-section__toggle-left">
          <Icon size={18} className="seo-section__icon" />
          <span>{title}</span>
          {badge != null && (
            <span className="seo-section__badge" style={{ background: badgeColor }}>{badge}</span>
          )}
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="seo-section__body">{children}</div>}
    </div>
  )
}

/* ── Finding row ── */
function Finding({ item }) {
  const colors = { error: '#ef4444', warning: '#f97316', success: '#22c55e' }
  const icons = {
    error: <XCircle size={16} />,
    warning: <AlertTriangle size={16} />,
    success: <CheckCircle size={16} />
  }
  const t = item.type || 'success'
  return (
    <div className="seo-finding" style={{ '--finding-color': colors[t] }}>
      <span className="seo-finding__icon">{icons[t]}</span>
      <div>
        <span className="seo-finding__msg">{item.message}</span>
        {item.category && <span className="seo-finding__cat">{item.category}</span>}
      </div>
    </div>
  )
}

/* ── Recommendation card ── */
function RecCard({ rec }) {
  const colors = { critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e' }
  const color = colors[rec.priority] || '#6b7280'
  return (
    <div className="seo-rec" style={{ '--rec-color': color }}>
      <div className="seo-rec__header">
        <span className="seo-rec__priority" style={{ background: color }}>{rec.priority}</span>
        <span className="seo-rec__cat">{rec.category}</span>
      </div>
      <h4 className="seo-rec__title">{rec.title}</h4>
      <p className="seo-rec__desc">{rec.description}</p>
      {rec.example && (
        <pre className="seo-rec__example"><code>{rec.example}</code></pre>
      )}
    </div>
  )
}

/* ── The 130-point scan grid data ── */
const scanCategories = [
  {
    icon: FileText, label: 'On-Page SEO', count: 28,
    checks: ['Title tag presence & length', 'Meta description presence & length', 'Meta keywords', 'H1 tag usage', 'H2/H3 heading structure', 'Keyword in title', 'Page word count', 'Content depth & paragraphs', 'Duplicate content signals', 'Reading level analysis']
  },
  {
    icon: Code, label: 'Technical SEO', count: 26,
    checks: ['HTTPS / SSL status', 'HTTP status code', 'Canonical tags', 'Robots meta tag', 'robots.txt presence', 'XML sitemap', 'Hreflang tags', 'Charset declaration', 'Structured data (JSON-LD)', 'Schema markup validity']
  },
  {
    icon: Smartphone, label: 'Mobile & UX', count: 18,
    checks: ['Viewport meta tag', 'Width=device-width', 'Responsive images (srcset)', 'Apple touch icons', 'Web app manifest', 'Touch target sizes', 'Font legibility', 'Tap element spacing']
  },
  {
    icon: Zap, label: 'Performance', count: 16,
    checks: ['Page response time', 'HTML file size', 'GZIP / Brotli compression', 'Cache-Control headers', 'Content encoding', 'Server response headers', 'Resource hints']
  },
  {
    icon: Image, label: 'Images', count: 14,
    checks: ['Alt text on all images', 'Image title attributes', 'Width & height attributes', 'Lazy loading (loading=lazy)', 'Image count & ratio', 'Descriptive file names', 'Next-gen formats']
  },
  {
    icon: Link2, label: 'Links', count: 12,
    checks: ['Internal link count', 'External link count', 'Nofollow link usage', 'Links without anchor text', 'Broken link indicators', 'Follow/nofollow ratio']
  },
  {
    icon: Share2, label: 'Social & Open Graph', count: 10,
    checks: ['OG title', 'OG description', 'OG image', 'OG URL', 'Twitter card type', 'Twitter title', 'Twitter description', 'Facebook App ID']
  },
  {
    icon: Globe, label: 'Accessibility & Language', count: 6,
    checks: ['HTML lang attribute', 'Skip navigation links', 'ARIA labels on interactive elements', 'Colour contrast signals', 'Focus management', 'Form labels']
  },
]

/* ── Download helper ── */
function buildTextReport(data) {
  const d = data
  const sep = '─'.repeat(60)
  const lines = [
    'RDS Studio — SEO Audit Report',
    `Generated: ${new Date().toLocaleString('en-ZA')}`,
    sep,
    `URL Scanned: ${d.url}`,
    `Overall Score: ${d.score}/100 — ${scoreLabel(d.score)}`,
    '',
    'CATEGORY SCORES',
    `  Metadata:  ${d.scores?.metadata ?? 'N/A'}/30`,
    `  Content:   ${d.scores?.content ?? 'N/A'}/30`,
    `  Technical: ${d.scores?.technical ?? 'N/A'}/40`,
    `  Mobile:    ${d.scores?.mobile ?? 'N/A'}/100`,
    '',
    sep,
    'CRITICAL ISSUES',
    ...(d.issues?.map(i => `  ✗ [${i.category}] ${i.message}`) || ['  None found.']),
    '',
    'WARNINGS',
    ...(d.warnings?.map(w => `  ⚠ [${w.category}] ${w.message}`) || ['  None found.']),
    '',
    'PASSED CHECKS',
    ...(d.successes?.map(s => `  ✓ [${s.category}] ${s.message}`) || ['  None found.']),
    '',
    sep,
    'RECOMMENDATIONS',
    ...(d.recommendations?.map((r, i) =>
      `\n  ${i + 1}. [${r.priority.toUpperCase()}] ${r.title}\n     ${r.description}${r.example ? `\n     Example: ${r.example}` : ''}`
    ) || ['  No recommendations.']),
    '',
    sep,
    'METADATA',
    `  Title:       ${d.metadata?.title || '(missing)'}`,
    `  Description: ${d.metadata?.description || '(missing)'}`,
    `  Canonical:   ${d.metadata?.canonical || '(missing)'}`,
    `  OG Title:    ${d.metadata?.ogTitle || '(missing)'}`,
    '',
    'CONTENT',
    `  H1 headings: ${d.content?.h1?.join(', ') || 'None'}`,
    `  H2 headings: ${d.content?.h2?.length ?? 0}`,
    `  Word count:  ${d.content?.wordCount ?? 0}`,
    `  Images:      ${d.images?.total ?? 0} (${d.images?.withoutAlt ?? 0} missing alt)`,
    `  Links:       ${d.links?.total ?? 0} (internal: ${d.links?.internal ?? 0}, external: ${d.links?.external ?? 0})`,
    '',
    'TECHNICAL',
    `  HTTPS:            ${d.technical?.hasHttps ? 'Yes' : 'No'}`,
    `  Structured data:  ${d.technical?.hasStructuredData ? 'Yes' : 'No'}`,
    `  Viewport tag:     ${d.technical?.hasViewport ? 'Yes' : 'No'}`,
    `  Compression:      ${d.technical?.hasCompression ? 'Yes' : 'No'}`,
    `  Response time:    ${d.technical?.responseTime || 'N/A'}`,
    '',
    sep,
    'Want these issues fixed? Contact RDS Studio:',
    '  https://rdsstudio.co.za/contact',
    '  info@rdsstudio.co.za | +27 84 639 0621',
    sep,
  ]
  return lines.join('\n')
}

/* ══════════════════════════════════════════════════════════
   Main Page
═══════════════════════════════════════════════════════════ */
export default function SEOToolPage() {
  const [url, setUrl]           = useState('')
  const [scanning, setScanning] = useState(false)
  const [scanStep, setScanStep] = useState('')
  const [result, setResult]     = useState(null)
  const [error, setError]       = useState('')
  const [activeTab, setActiveTab] = useState('issues')
  const resultRef = useRef(null)

  const steps = [
    'Connecting to your website…',
    'Checking on-page SEO elements…',
    'Analysing technical signals…',
    'Evaluating mobile friendliness…',
    'Auditing images and links…',
    'Scoring performance metrics…',
    'Compiling your 130-point report…',
  ]

  const runScan = useCallback(async () => {
    const trimmed = url.trim()
    if (!trimmed) { setError('Please enter a website URL.'); return }
    setError('')
    setResult(null)
    setScanning(true)

    // Animate scan steps
    let stepIdx = 0
    setScanStep(steps[0])
    const timer = setInterval(() => {
      stepIdx = Math.min(stepIdx + 1, steps.length - 1)
      setScanStep(steps[stepIdx])
    }, 900)

    try {
      const fullUrl = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`
      const resp = await fetch(`${API_BASE}/seo/public-scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: fullUrl })
      })
      const json = await resp.json()
      clearInterval(timer)
      setScanning(false)
      if (!json.success) {
        setError(json.error || 'Scan failed. Please try again.')
      } else {
        setResult(json.data)
        setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200)
      }
    } catch (e) {
      clearInterval(timer)
      setScanning(false)
      setError('Network error — please check your connection and try again.')
    }
  }, [url])

  const handleKeyDown = (e) => { if (e.key === 'Enter') runScan() }

  const downloadReport = () => {
    if (!result) return
    const text = buildTextReport(result)
    const blob = new Blob([text], { type: 'text/plain' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    const domain = result.url.replace(/https?:\/\/(www\.)?/, '').split('/')[0]
    a.download = `seo-audit-${domain}-${new Date().toISOString().slice(0, 10)}.txt`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const findings = result
    ? [
        ...(result.issues || []).map(i => ({ ...i, type: 'error' })),
        ...(result.warnings || []).map(w => ({ ...w, type: 'warning' })),
        ...(result.successes || []).map(s => ({ ...s, type: 'success' }))
      ]
    : []

  return (
    <div>
      <PageMeta
        title="Free SEO Audit Tool — 130-Point Website Scanner | RDS Studio"
        description="Scan your website with RDS Studio's free 130-point SEO audit tool. Get a full report on on-page SEO, technical issues, mobile, performance, and more — instantly."
        canonical="https://rdsstudio.co.za/seo-audit"
      />
      <Header />

      {/* ── Hero ── */}
      <section className="seo-hero">
        <div className="seo-hero__inner">
          <div className="seo-hero__badge">
            <Star size={13} />
            Free &amp; Instant — No login required
          </div>
          <h1 className="seo-hero__title">
            Free 130-Point<br />
            <span className="seo-hero__accent">SEO Audit Tool</span>
          </h1>
          <p className="seo-hero__sub">
            Paste your URL and get a complete SEO health report in seconds. We check on-page SEO,
            technical signals, mobile, performance, images, links and more — across 130 individual criteria.
          </p>

          {/* URL input */}
          <div className="seo-input-wrap">
            <Globe size={18} className="seo-input-wrap__icon" />
            <input
              className="seo-input"
              type="url"
              placeholder="https://yourwebsite.com"
              value={url}
              onChange={e => setUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-label="Website URL to scan"
              disabled={scanning}
            />
            <button
              className={`seo-scan-btn ${scanning ? 'scanning' : ''}`}
              onClick={runScan}
              disabled={scanning}
              aria-label="Run SEO scan"
            >
              {scanning
                ? <><RefreshCw size={16} className="spin" /> Scanning…</>
                : <><Search size={16} /> Scan Now</>
              }
            </button>
          </div>

          {error && <p className="seo-error">{error}</p>}

          {/* Scan progress */}
          {scanning && (
            <div className="seo-progress" aria-live="polite">
              <div className="seo-progress__bar"><div className="seo-progress__fill" /></div>
              <p className="seo-progress__step">{scanStep}</p>
            </div>
          )}

          {/* Trust badges */}
          <div className="seo-trust">
            <span><CheckCircle size={13} /> 130 checks</span>
            <span><CheckCircle size={13} /> Instant results</span>
            <span><CheckCircle size={13} /> No credit card</span>
            <span><CheckCircle size={13} /> Downloadable report</span>
          </div>
        </div>
      </section>

      {/* ── Results ── */}
      {result && (
        <section className="seo-results" ref={resultRef}>
          <div className="seo-results__inner">

            {/* Score overview row */}
            <div className="seo-overview">
              <div className="seo-overview__gauge-col">
                <ScoreGauge score={result.score} />
                <p className="seo-overview__url" title={result.url}>
                  {result.url.replace(/^https?:\/\//, '')}
                </p>
              </div>

              <div className="seo-overview__categories">
                <h2 className="seo-overview__title">SEO Health Score</h2>
                <p className="seo-overview__sub">
                  {result.issues?.length} critical issue{result.issues?.length !== 1 ? 's' : ''} &nbsp;·&nbsp;
                  {result.warnings?.length} warning{result.warnings?.length !== 1 ? 's' : ''} &nbsp;·&nbsp;
                  {result.successes?.length} passed
                </p>
                <div className="seo-cats">
                  <CategoryBar label="Metadata" score={result.scores?.metadata ?? 0} max={30} icon={FileText} />
                  <CategoryBar label="Content" score={result.scores?.content ?? 0} max={30} icon={BarChart2} />
                  <CategoryBar label="Technical" score={result.scores?.technical ?? 0} max={40} icon={Code} />
                  <CategoryBar label="Mobile" score={result.scores?.mobile ?? 0} max={100} icon={Smartphone} />
                </div>
              </div>

              <div className="seo-overview__actions">
                <button className="seo-dl-btn" onClick={downloadReport}>
                  <Download size={16} /> Download Report
                </button>
                <button className="seo-rescan-btn" onClick={runScan}>
                  <RefreshCw size={14} /> Re-scan
                </button>
                <Link to="/contact" className="seo-fix-btn">
                  Fix These Issues <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            {/* Tabs: Issues / Warnings / Passed */}
            <div className="seo-tabs">
              {[
                { id: 'issues', label: `Issues (${result.issues?.length ?? 0})`, color: '#ef4444' },
                { id: 'warnings', label: `Warnings (${result.warnings?.length ?? 0})`, color: '#f97316' },
                { id: 'passed', label: `Passed (${result.successes?.length ?? 0})`, color: '#22c55e' },
                { id: 'recs', label: `Recommendations (${result.recommendations?.length ?? 0})`, color: '#2DD4BF' },
              ].map(tab => (
                <button
                  key={tab.id}
                  className={`seo-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                  style={activeTab === tab.id ? { borderBottomColor: tab.color, color: tab.color } : {}}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="seo-findings-list">
              {activeTab === 'issues' && (
                result.issues?.length
                  ? result.issues.map((item, i) => <Finding key={i} item={{ ...item, type: 'error' }} />)
                  : <p className="seo-empty"><CheckCircle size={18} /> No critical issues found — great work!</p>
              )}
              {activeTab === 'warnings' && (
                result.warnings?.length
                  ? result.warnings.map((item, i) => <Finding key={i} item={{ ...item, type: 'warning' }} />)
                  : <p className="seo-empty"><CheckCircle size={18} /> No warnings found.</p>
              )}
              {activeTab === 'passed' && (
                result.successes?.length
                  ? result.successes.map((item, i) => <Finding key={i} item={{ ...item, type: 'success' }} />)
                  : <p className="seo-empty">No passed checks yet.</p>
              )}
              {activeTab === 'recs' && (
                result.recommendations?.length
                  ? result.recommendations.map((rec, i) => <RecCard key={i} rec={rec} />)
                  : <p className="seo-empty"><CheckCircle size={18} /> No recommendations — your site is in great shape!</p>
              )}
            </div>

            {/* Detailed data sections */}
            <div className="seo-detail-sections">

              <Section title="Metadata" icon={FileText} defaultOpen badge={null}>
                <div className="seo-meta-grid">
                  {[
                    { label: 'Title', value: result.metadata?.title, ideal: '30–60 chars', length: result.metadata?.title?.length },
                    { label: 'Meta Description', value: result.metadata?.description, ideal: '120–160 chars', length: result.metadata?.description?.length },
                    { label: 'Canonical URL', value: result.metadata?.canonical },
                    { label: 'Robots', value: result.metadata?.robots },
                    { label: 'Language', value: result.metadata?.lang },
                    { label: 'Viewport', value: result.metadata?.viewport },
                    { label: 'OG Title', value: result.metadata?.ogTitle },
                    { label: 'OG Description', value: result.metadata?.ogDescription },
                    { label: 'OG Image', value: result.metadata?.ogImage },
                    { label: 'Twitter Card', value: result.metadata?.twitterCard },
                  ].map(({ label, value, ideal, length }) => (
                    <div key={label} className="seo-meta-row">
                      <span className="seo-meta-row__label">{label}</span>
                      <div>
                        <span className={`seo-meta-row__value ${!value ? 'missing' : ''}`}>
                          {value || '(missing)'}
                        </span>
                        {ideal && value && (
                          <span className="seo-meta-row__hint">{length} chars · ideal {ideal}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Content Analysis" icon={BarChart2}>
                <div className="seo-stats-grid">
                  <div className="seo-stat"><span>{result.content?.wordCount ?? 0}</span>Words</div>
                  <div className="seo-stat"><span>{result.content?.paragraphs ?? 0}</span>Paragraphs</div>
                  <div className="seo-stat"><span>{result.content?.h1?.length ?? 0}</span>H1 Tags</div>
                  <div className="seo-stat"><span>{result.content?.h2?.length ?? 0}</span>H2 Tags</div>
                  <div className="seo-stat"><span>{result.content?.h3?.length ?? 0}</span>H3 Tags</div>
                  <div className="seo-stat"><span>{result.links?.total ?? 0}</span>Total Links</div>
                </div>
                {result.content?.h1?.length > 0 && (
                  <div className="seo-h1-list">
                    <p className="seo-detail-label">H1 Headings Found</p>
                    {result.content.h1.map((h, i) => <div key={i} className="seo-h1-item">"{h}"</div>)}
                  </div>
                )}
              </Section>

              <Section title="Technical SEO" icon={Code}>
                <div className="seo-tech-grid">
                  {[
                    { label: 'HTTPS', pass: result.technical?.hasHttps },
                    { label: 'Canonical Tag', pass: result.technical?.hasCanonical },
                    { label: 'Viewport Tag', pass: result.technical?.hasViewport },
                    { label: 'Charset', pass: result.technical?.hasCharset },
                    { label: 'Structured Data', pass: result.technical?.hasStructuredData },
                    { label: 'Hreflang', pass: result.technical?.hasHreflang },
                    { label: 'Compression', pass: result.technical?.hasCompression },
                  ].map(({ label, pass }) => (
                    <div key={label} className={`seo-tech-item ${pass ? 'pass' : 'fail'}`}>
                      {pass ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
                <div className="seo-tech-meta">
                  <span>Status: <strong>{result.technical?.statusCode}</strong></span>
                  <span>Response: <strong>{result.technical?.responseTime}</strong></span>
                  <span>Server: <strong>{result.technical?.serverHeader || 'N/A'}</strong></span>
                </div>
              </Section>

              <Section title="Images" icon={Image}>
                <div className="seo-stats-grid">
                  <div className="seo-stat"><span>{result.images?.total ?? 0}</span>Total</div>
                  <div className="seo-stat ok"><span>{result.images?.withAlt ?? 0}</span>With Alt</div>
                  <div className="seo-stat warn"><span>{result.images?.withoutAlt ?? 0}</span>Missing Alt</div>
                  <div className="seo-stat"><span>{result.images?.withLazyLoad ?? 0}</span>Lazy Loaded</div>
                </div>
              </Section>

              <Section title="Links" icon={Link2}>
                <div className="seo-stats-grid">
                  <div className="seo-stat"><span>{result.links?.total ?? 0}</span>Total Links</div>
                  <div className="seo-stat"><span>{result.links?.internal ?? 0}</span>Internal</div>
                  <div className="seo-stat"><span>{result.links?.external ?? 0}</span>External</div>
                  <div className="seo-stat"><span>{result.links?.nofollow ?? 0}</span>Nofollow</div>
                  <div className="seo-stat warn"><span>{result.links?.withoutText ?? 0}</span>No Anchor Text</div>
                </div>
              </Section>

              <Section title="Performance" icon={Zap}>
                <div className="seo-stats-grid">
                  <div className="seo-stat"><span>{result.performance?.htmlSizeKB ?? 0} KB</span>HTML Size</div>
                  <div className="seo-stat"><span>{result.performance?.responseTime ?? 'N/A'}</span>Response</div>
                  <div className={`seo-stat ${result.performance?.hasCompression ? 'ok' : 'warn'}`}>
                    <span>{result.performance?.hasCompression ? 'Yes' : 'No'}</span>Compression
                  </div>
                  <div className="seo-stat"><span>{result.performance?.cacheControl || 'none'}</span>Cache-Control</div>
                </div>
              </Section>

              <Section title="Mobile Friendliness" icon={Smartphone}>
                <div className="seo-tech-grid">
                  {[
                    { label: 'Viewport Tag', pass: result.mobile?.hasViewport },
                    { label: 'Device-width', pass: result.mobile?.viewportContent?.includes('device-width') },
                    { label: 'Responsive Images', pass: result.mobile?.hasResponsiveImages },
                    { label: 'Touch Icons', pass: result.mobile?.hasTouchIcons },
                    { label: 'Web Manifest', pass: result.mobile?.hasManifest },
                  ].map(({ label, pass }) => (
                    <div key={label} className={`seo-tech-item ${pass ? 'pass' : 'fail'}`}>
                      {pass ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </Section>

              {result.structured && (
                <Section title="Structured Data / Schema" icon={Shield}>
                  {result.structured.jsonLd?.length > 0 ? (
                    <div className="seo-schema-list">
                      {result.structured.jsonLd.map((s, i) => (
                        <div key={i} className={`seo-schema-item ${s.valid ? 'pass' : 'fail'}`}>
                          {s.valid ? <CheckCircle size={14} /> : <XCircle size={14} />}
                          <span>{s.type}</span>
                          {!s.valid && <span className="seo-schema-err">{s.error}</span>}
                        </div>
                      ))}
                      {result.structured.microdata > 0 && (
                        <p className="seo-detail-label" style={{ marginTop: 8 }}>Microdata elements: {result.structured.microdata}</p>
                      )}
                    </div>
                  ) : (
                    <p className="seo-empty"><AlertTriangle size={16} /> No structured data found on this page.</p>
                  )}
                </Section>
              )}
            </div>

            {/* CTA */}
            <div className="seo-cta">
              <div className="seo-cta__inner">
                <TrendingUp size={32} className="seo-cta__icon" />
                <h3>Want these issues fixed by professionals?</h3>
                <p>Our team specialises in technical SEO, on-page optimisation, and Google rankings for South African businesses. Let's review your full audit together — free consultation.</p>
                <div className="seo-cta__btns">
                  <Link to="/contact" className="seo-cta__primary">Book a Free SEO Consultation <ArrowRight size={16} /></Link>
                  <button className="seo-cta__secondary" onClick={downloadReport}>
                    <Download size={15} /> Download Your Report
                  </button>
                </div>
              </div>
            </div>

          </div>
        </section>
      )}

      {/* ── How it works (shown when no result yet) ── */}
      {!result && !scanning && (
        <section className="seo-how">
          <div className="seo-how__inner">
            <h2 className="seo-how__title">How It Works</h2>
            <p className="seo-how__sub">Your website is scanned the same way a professional SEO auditor would — automatically.</p>
            <div className="seo-steps">
              {[
                { n: '01', icon: Globe, title: 'Enter Your URL', desc: 'Paste your website address above. We accept any live website — no login or account needed.' },
                { n: '02', icon: Search, title: 'We Run 130 Checks', desc: 'Our crawler visits your page and evaluates 130 individual SEO criteria across 8 categories in seconds.' },
                { n: '03', icon: FileText, title: 'Get Your Full Report', desc: 'Review your score, see every issue and passed check, then download a plain-text report to share with your team.' },
              ].map(({ n, icon: Icon, title, desc }) => (
                <div key={n} className="seo-step">
                  <div className="seo-step__num">{n}</div>
                  <Icon size={28} className="seo-step__icon" />
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 130-point scan breakdown ── */}
      <section className="seo-breakdown">
        <div className="seo-breakdown__inner">
          <h2 className="seo-breakdown__title">What Our 130-Point Scan Checks</h2>
          <p className="seo-breakdown__sub">
            Every scan evaluates your website across {scanCategories.reduce((a, c) => a + c.count, 0)} individual data points
            — the same criteria Google and major search engines use to rank websites.
          </p>
          <div className="seo-breakdown__grid">
            {scanCategories.map(({ icon: Icon, label, count, checks }) => (
              <div key={label} className="seo-breakdown__card">
                <div className="seo-breakdown__card-header">
                  <Icon size={20} className="seo-breakdown__card-icon" />
                  <span className="seo-breakdown__card-label">{label}</span>
                  <span className="seo-breakdown__card-count">{count} checks</span>
                </div>
                <ul className="seo-breakdown__checks">
                  {checks.map(c => <li key={c}><CheckCircle size={11} />{c}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why SEO matters ── */}
      <section className="seo-why">
        <div className="seo-why__inner">
          <h2 className="seo-why__title">Why Your SEO Score Matters</h2>
          <div className="seo-why__grid">
            {[
              { icon: TrendingUp, title: '68% of online experiences begin with a search engine', desc: 'Ranking on the first page of Google is the single most effective form of organic marketing for South African businesses.' },
              { icon: Clock, title: 'Technical issues kill rankings silently', desc: 'Slow load times, missing meta tags, and broken structure signals can suppress your rankings without any visible warning.' },
              { icon: Smartphone, title: 'Google ranks mobile-first', desc: 'Since 2021 Google uses your mobile site as the primary version for ranking. Mobile SEO is not optional.' },
              { icon: Shield, title: 'Structured data earns rich snippets', desc: 'Schema markup can get your site featured in answer boxes, star ratings, and knowledge panels — free traffic with higher click rates.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="seo-why__card">
                <Icon size={22} className="seo-why__icon" />
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
          <div className="seo-why__cta">
            <Link to="/services" className="seo-why__link">View our SEO &amp; Digital Marketing services <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
